// ═══════════════════════════════════════════════════════════════
// HOOK: useContracts
// Hook personalizado para gestionar contratos
// ═══════════════════════════════════════════════════════════════

import { useState, useCallback } from 'react';
import { contractsAPI } from '../api/contracts';

export const useContracts = () => {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Cargar todos los contratos
  const loadContracts = useCallback(async (filters = {}) => {
    setLoading(true);
    setError(null);
    try {
      const data = await contractsAPI.getAll(filters);
      if (data.success) {
        setContracts(data.contracts || []);
        return { success: true, contracts: data.contracts };
      } else {
        setError(data.error);
        return { success: false, error: data.error };
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Error al cargar contratos';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  // Cargar contratos próximos a vencer
  const loadExpiring = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await contractsAPI.getExpiring();
      if (data.success) {
        return { success: true, contracts: data.contracts };
      } else {
        setError(data.error);
        return { success: false, error: data.error };
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Error al cargar contratos por vencer';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  // Cargar historial de un empleado
  const loadEmployeeHistory = useCallback(async (employeeId) => {
    setLoading(true);
    setError(null);
    try {
      const data = await contractsAPI.getEmployeeHistory(employeeId);
      if (data.success) {
        return { success: true, contracts: data.contracts };
      } else {
        setError(data.error);
        return { success: false, error: data.error };
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Error al cargar historial';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  // Crear contrato
  const createContract = useCallback(async (contractData) => {
    setLoading(true);
    setError(null);
    try {
      const data = await contractsAPI.create(contractData);
      if (data.success) {
        return { success: true, contract: data.contract };
      } else {
        setError(data.error);
        return { success: false, error: data.error };
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Error al crear contrato';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  // Actualizar contrato
  const updateContract = useCallback(async (id, contractData) => {
    setLoading(true);
    setError(null);
    try {
      const data = await contractsAPI.update(id, contractData);
      if (data.success) {
        return { success: true, contract: data.contract };
      } else {
        setError(data.error);
        return { success: false, error: data.error };
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Error al actualizar contrato';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  // Eliminar (rescindir) contrato
  const deleteContract = useCallback(async (id, motivo) => {
    setLoading(true);
    setError(null);
    try {
      const data = await contractsAPI.delete(id, motivo);
      if (data.success) {
        return { success: true };
      } else {
        setError(data.error);
        return { success: false, error: data.error };
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Error al eliminar contrato';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  // Obtener estadísticas
  const loadStats = useCallback(async () => {
    try {
      const data = await contractsAPI.getStats();
      if (data.success) {
        return { success: true, stats: data.stats };
      } else {
        return { success: false, error: data.error };
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Error al cargar estadísticas';
      return { success: false, error: errorMsg };
    }
  }, []);

  return {
    contracts,
    loading,
    error,
    loadContracts,
    loadExpiring,
    loadEmployeeHistory,
    createContract,
    updateContract,
    deleteContract,
    loadStats
  };
};
